/**
 * Rep-facing reward-status label for the Event Operations roster (SPEC step 7).
 *
 * Extracted from EventOperationsTab so the label matrix is unit-testable in isolation.
 *
 * TICKET-ONLY (award_kind='none') correctness — the bug this addresses:
 *   A ticket-only event provisions a $0 is_placeholder admission offer with
 *   auto_escalate=false / escalation_offer_id=NULL (migration 2026-08-11-007). At
 *   registration, issue-event-ticket still mints+attaches that $0 placeholder as the
 *   ticket's reward voucher with reward_state='pending' — EXACTLY the same shape a
 *   configured-reward event uses before check-in. So the roster showed "Reward pending"
 *   for a ticket-only registrant even though NO reward is coming automatically (the
 *   ticket never escalates; an operator must deliberately +$20 on-site). That label is
 *   misleading for a ticket-only event.
 *
 *   Fix (labeling only — no issuance/behaviour change): when the event is ticket-only
 *   (award_kind='none') and the reward is a plain 'pending' placeholder that is NOT
 *   flagged as needing a fix, show "Ticket issued — no reward assigned" instead of
 *   "Reward pending". A configured-reward event (award_kind != 'none') is UNTOUCHED and
 *   still shows "Reward pending"; a genuinely owed award (needs_reward_fix, e.g. a guest
 *   award on a ticket-only event) still shows "Needs award". Once an operator ratchets
 *   (+$20) the voucher flips to reward_state='assigned', so the ticket-only branch no
 *   longer applies — the assigned-award label takes over.
 *
 * This also correctly relabels LEGACY $0 pending placeholder rows minted before this fix:
 * the label keys on the live event award_kind + voucher state, so no data rewrite is
 * needed and existing placeholders remain ratchetable.
 */
import type { OperationsRosterRow } from "../services/eventOperations.service";

export interface RewardStatusBadge {
  text: string;
  cls: string;
}

/** Neutral "no reward is owed" styling — deliberately NOT amber (amber signals an
 *  operator action is pending/needed; a ticket-only registrant needs no reward action). */
const NEUTRAL = "bg-gray-100 text-gray-600";

/**
 * Compute the rep-facing reward badge for a roster row.
 *
 * @param row          the roster row from get_event_operations_roster.
 * @param isTicketOnly the event's award_kind === 'none' (ticket-only / no configured
 *                     reward). Read once per event from event_ticketing_provisioning.
 *                     Defaults false so a configured-reward event (or an unknown/loading
 *                     award_kind) behaves byte-identically to the pre-fix label.
 */
export function rewardStatus(
  row: OperationsRosterRow,
  isTicketOnly = false,
): RewardStatusBadge {
  if (row.overridable_reason === "reward already redeemed") {
    return { text: "Reward redeemed", cls: "bg-blue-100 text-blue-800" };
  }
  if (row.overridable_reason === "reward already cancelled") {
    return { text: "Reward cancelled", cls: NEUTRAL };
  }
  if (!row.reward_state || row.reward_state === "void") {
    return {
      text: row.reward_state === "void" ? "Reward void" : "No reward yet",
      cls: NEUTRAL,
    };
  }
  if (row.reward_state === "pending") {
    if (row.needs_reward_fix) {
      // A specific award IS owed (e.g. a guest award on a ticket-only event) — surface it
      // regardless of ticket-only, so the operator still fixes it.
      return { text: "Needs award", cls: "bg-amber-100 text-amber-800" };
    }
    if (isTicketOnly) {
      // Ticket-only event: the $0 placeholder is admission bookkeeping, not a pending
      // reward. Say so honestly instead of implying a reward is on the way.
      return { text: "Ticket issued — no reward assigned", cls: NEUTRAL };
    }
    return { text: "Reward pending", cls: "bg-amber-50 text-amber-700" };
  }
  if (row.reward_state === "assigned") {
    return row.needs_reward_fix
      ? { text: "Wrong award — fix", cls: "bg-amber-100 text-amber-800" }
      : {
          text: row.assigned_offer_name ?? "Reward set",
          cls: "bg-green-100 text-green-800",
        };
  }
  return { text: row.reward_state, cls: NEUTRAL };
}
